/**
 * Admin-only route prefixes mapped to their UserPermission section
 * key. Used by `AuthGuard` to decide whether a non-admin can be
 * granted access to a path via a section.read overlay grant.
 *
 * Paths NOT in this map (`/permissions`, `/admin-dashboard`,
 * `/payroll`, `/promote`, `/attendance`) are intentionally
 * schooladmin-only — a section grant cannot unlock them.
 * (`/hostel`, `/library`, `/sports` and `/transport` ARE
 * grant-unlockable via their sections — a warden / librarian /
 * coach / bus crew, or an involved student or borrower, reaches
 * them through the implicit reveal in /api/permissions/me.)
 *
 * `skip` lets us carve out sub-paths under the same prefix that
 * have their own broader access rule (e.g. /examinations/results
 * is open to teacher + student).
 */
export const ADMIN_PATH_SECTION_MAP = [
  { prefix: '/students', section: 'students' },
  { prefix: '/teachers', section: 'staff' },
  { prefix: '/subject', section: 'subjects' },
  {
    prefix: '/examinations',
    section: 'examinations',
    skip: (p) =>
      p.startsWith('/examinations/results') ||
      p.startsWith('/examinations/schedule'),
  },
  { prefix: '/fees-report', section: 'reports' },
  { prefix: '/fees', section: 'fees' },
  { prefix: '/payroll', section: 'payroll' },
  { prefix: '/staffs', section: 'staff' },
  { prefix: '/departments', section: 'departments' },
  { prefix: '/designation', section: 'staff' },
  { prefix: '/settings', section: 'settings' },
  { prefix: '/activity-logs', section: 'activity-logs' },
  { prefix: '/hostel', section: 'hostel' },
  { prefix: '/library', section: 'library' },
  { prefix: '/sports', section: 'sports' },
  { prefix: '/transport', section: 'transport' },
  { prefix: '/data-export', section: 'data-export' },
  { prefix: '/pickup', section: 'pickup' },
];

/**
 * Resolve the section key for an admin-only path, or null when
 * the path is either (a) not admin-only, or (b) intentionally
 * not overridable via a section grant.
 *
 * Special-cases:
 *   - `/staff` exact and `/staff/*` map to 'staff', but
 *     `/staff-dashboard` does NOT — that's the dynamic-staff
 *     landing page, not the admin-side staff list.
 *   - `/classes/list` (and any deeper sub-routes under it) maps
 *     to 'classes' — the rest of `/classes/*` is open to teachers
 *     and students by default.
 */
export const matchAdminPathSection = (pathname) => {
  if (!pathname) return null;
  if (
    pathname === '/staff' ||
    (pathname.startsWith('/staff/') && !pathname.startsWith('/staff-dashboard'))
  ) {
    return 'staff';
  }
  if (pathname === '/classes/list' || pathname.startsWith('/classes/list/')) {
    return 'classes';
  }
  for (const m of ADMIN_PATH_SECTION_MAP) {
    if (m.skip?.(pathname)) continue;
    if (pathname.startsWith(m.prefix)) return m.section;
  }
  return null;
};

// ─── Permissions response helpers ────────────────────────────────────────
//
// Pure functions deliberately kept out of `usePermissions.js` (which
// has React + alias imports) so unit tests can import them with the
// stock node:test runner — no Next.js, no jsdom, no babel.

const sectionsToMap = (sections) => {
  const map = {};
  if (!Array.isArray(sections)) return map;
  for (const row of sections) {
    if (!row?.section) continue;
    map[row.section] = {
      read: !!row.read,
      write: !!row.write,
      update: !!row.update,
      delete: !!row.delete,
    };
  }
  return map;
};

/**
 * Build the `can.{read,write,update,delete}(section)` helper bundle
 * from a permissions snapshot. Schooladmin gets unconditional true.
 */
export const buildCanHelpers = (state) => {
  const isAdmin = !!state?.isSchoolAdmin;
  const map = sectionsToMap(state?.sections);
  const op = (section, key) => {
    if (isAdmin) return true;
    return !!map[section]?.[key];
  };
  return {
    read: (section) => op(section, 'read'),
    write: (section) => op(section, 'write'),
    update: (section) => op(section, 'update'),
    delete: (section) => op(section, 'delete'),
  };
};
